import classNames from 'classnames';
import { FC } from 'react';
import { useDrop } from 'react-dnd';

import { CollectionIcon, MailIcon } from '@heroicons/react/outline';
import { getTimeStamp } from '../../../../lib/time';
import { ProviderCollection } from '../../../../types/providerCollection';
import ActionButton from '../../../ActionButton';
import ActionButtonGroup from '../../../ActionButtonGroup';
import { ActivityListDataFragment } from '../../../../../generated/graphql';
import { DragTypes } from '../../../../lib/constants';

const ProviderCollectionTableRow: FC<{
  providerCollection: ProviderCollection;
  setSelectedProviderCollection: React.Dispatch<
    React.SetStateAction<ProviderCollection | undefined>
  >;
  followUpMode: boolean;
  sendProviderActivitiesInFollowUp?: (
    providerActivities: ActivityListDataFragment[],
  ) => void;
  providerActivitiesInCollection: ActivityListDataFragment[] | undefined;
  addActivityToProviderCollection: (
    providerCollection: ProviderCollection,
    providerActivity: ActivityListDataFragment,
  ) => Promise<void>;
}> = ({
  providerCollection,
  setSelectedProviderCollection,
  followUpMode,
  sendProviderActivitiesInFollowUp,
  providerActivitiesInCollection,
  addActivityToProviderCollection,
}) => {
  const onClick = () => {
    setSelectedProviderCollection(providerCollection);
  };

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: DragTypes.LIBRARY_ITEM,
    drop: (item: {
      providerActivity: ActivityListDataFragment;
      id: string;
    }) => {
      return addActivityToProviderCollection(
        providerCollection,
        item.providerActivity,
      );
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <tr
      ref={drop}
      className={classNames(
        'max-h-[48px] cursor-pointer text-ellipsis border-b border-neutral-50 text-caption text-secondary-150 hover:bg-neutral-25',
        isOver && canDrop && 'bg-secondary-20',
      )}
      onClick={onClick}
    >
      <td className="w-12 py-0 pl-6 pr-4 sm:w-16 sm:px-8"></td>
      <td className={classNames(!followUpMode ? 'w-2/3' : 'w-1/2')}>
        <div className="flex items-center">
          <div className="mr-4 flex h-[36px] w-[36px] flex-row items-center justify-center rounded-lg">
            <CollectionIcon className="h-[30px] w-[30px] text-secondary-150" />
          </div>
          <div
            className={classNames(
              'font-medium text-neutral-150',
              !followUpMode ? 'line-clamp-2' : 'line-clamp-1',
            )}
          >
            {providerCollection.name}
          </div>
        </div>
      </td>

      <td className="w-1/3">
        <div className="text-caption">
          {getTimeStamp(providerCollection.createdAt, false)}
        </div>
      </td>
      <td className="min-w-[120px]">
        <div className="-mt-px flex h-12 items-center justify-end pr-6">
          <ActionButtonGroup size="small">
            {sendProviderActivitiesInFollowUp && (
              <ActionButton
                onClick={(e) => {
                  e.stopPropagation();
                  sendProviderActivitiesInFollowUp?.(
                    providerActivitiesInCollection ?? [],
                  );
                }}
                title={
                  followUpMode ? 'Attach this collection' : 'Send to clients'
                }
              >
                <MailIcon className="h-5 w-5 text-neutral-125" />
              </ActionButton>
            )}
          </ActionButtonGroup>
        </div>
      </td>
    </tr>
  );
};

export default ProviderCollectionTableRow;
